import type { ReactElement } from 'react';
import { FormParagraph } from '@ksyos/design-system';
import { getProgrammeList } from '@frontend/components/pages/episodeCategory/therapielandProgrammes/getProgrammes';
import type { ICustomControlDiagnosticReportReadProps } from '../../interfaces/customControlDiagnosticReportReadProps';
import type { IKsyosMedicalCenterCustomControlDataFields } from './Osasc2V2KsyosMedicalCenterCustomControlFields';
import { translateKey } from '../../../../../translate';
import { FormDivider, HeaderDivider } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { List, ListItem } from '../../../../elements/List/List';
import { NullableFormattedDate } from '../../../../elements/NullableFormattedDate';
import { getToesturenValues } from './getToesturenValues';

export function Osasc2V2DiagnosticReportReadKsyosMedicalCenter(props: ICustomControlDiagnosticReportReadProps) {
    const fields: IKsyosMedicalCenterCustomControlDataFields = props.diagnosticReport.customControlData?.fields ?? {};

    return (
        <>
            <FormDivider />

            {fields.dateConsult && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('diagnosticReport.dateConsult')}</FormLabel>
                    <InputContainer className="mb-3">
                        <FormParagraph>
                            <NullableFormattedDate value={fields.dateConsult} />
                        </FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {(fields.aanvullendeAnamnese || fields.aanvullendLichamelijkOnderzoek) && (
                <HeaderDivider text="Aanvullende specialistische bevindingen" />
            )}

            {fields.aanvullendeAnamnese && (
                <HorizontalFormGroupRead>
                    <FormLabel>Aanvullende anamnese</FormLabel>
                    <InputContainer>
                        <FormParagraph>{fields.aanvullendeAnamnese}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {fields.aanvullendLichamelijkOnderzoek && (
                <HorizontalFormGroupRead>
                    <FormLabel>Aanvullend lichamelijk onderzoek</FormLabel>
                    <InputContainer>
                        <FormParagraph>{fields.aanvullendLichamelijkOnderzoek}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            <FormDivider />
            <HeaderDivider
                text="Bevindingen slaaponderzoek"
                tooltipText={
                    <>
                        <ul>
                            <li className="listheader">Apneu-Hypopneu Index (AHI)</li>
                            <li className="no-style">Aantal (bijna) ademstops per uur slaap</li>
                        </ul>
                        <ul>
                            <li className="listheader">Oxygen Desaturation Index (ODI)</li>
                            <li className="no-style">Aantal zuurstofdalingen ≥3% of ≥4% per uur slaap</li>
                        </ul>
                        <ul>
                            <li className="listheader">Positieafhankelijk</li>
                            <li className="no-style">
                                Het aantal (bijna) ademstops per uur slaap (AHI) is in rugligging minimaal twee keer zo
                                hoog als in een andere slaaphouding
                            </li>
                        </ul>
                    </>
                }
            />

            {fields.gemiddeldeAhi !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>Gemiddelde AHI</FormLabel>
                    <InputContainer>
                        <FormParagraph>{fields.gemiddeldeAhi}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {fields.gemiddeldeOdi !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>Gemiddelde ODI 3%</FormLabel>
                    <InputContainer>
                        <FormParagraph>{fields.gemiddeldeOdi}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {fields.odi4 !== null && fields.odi4 !== undefined && (
                <HorizontalFormGroupRead>
                    <FormLabel>Gemiddelde ODI 4%</FormLabel>
                    <InputContainer>
                        <FormParagraph>{fields.odi4}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {fields.gemiddeldeSaturatie !== null && fields.gemiddeldeSaturatie !== undefined && (
                <HorizontalFormGroupRead>
                    <FormLabel>Gemiddelde saturatie %</FormLabel>
                    <InputContainer>
                        <FormParagraph>{fields.gemiddeldeSaturatie}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {fields.laagstGemetenSaturatie !== null && fields.laagstGemetenSaturatie !== undefined && (
                <HorizontalFormGroupRead>
                    <FormLabel>Laagst gemeten saturatie %</FormLabel>
                    <InputContainer>
                        <FormParagraph>{fields.laagstGemetenSaturatie}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {fields.relevanteHypoxemie !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>Relevante Hypoxemie</FormLabel>
                    <InputContainer>
                        <FormParagraph>{fields.relevanteHypoxemie ? 'Ja' : 'Nee'}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {fields.relevanteHypoxemieRemarks !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('common.remarks')}</FormLabel>
                    <InputContainer>
                        <FormParagraph>{fields.relevanteHypoxemieRemarks}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {fields.positieafhankelijk !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>Positieafhankelijk</FormLabel>
                    <InputContainer>
                        <FormParagraph>{fields.positieafhankelijk}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {fields.positieafhankelijkRemarks !== null && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('common.remarks')}</FormLabel>
                    <InputContainer>
                        <FormParagraph>{fields.positieafhankelijkRemarks}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {showRelevanteBevindingen()}

            <FormDivider />

            {fields.osa !== null && (
                <>
                    <HeaderDivider text={translateKey('common.conclusion')} />
                    <HorizontalFormGroupRead>
                        <FormLabel>Obstructieve Slaapapneu (OSA)</FormLabel>
                        <InputContainer>
                            <FormParagraph>{fields.osa ? 'Ja' : 'Nee'}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                    {fields.osa ? showOsaYesFlow() : showOsaNoFlow()}
                </>
            )}
        </>
    );

    function showOsaYesFlow() {
        return (
            <>
                {fields.mateVanOsa !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Mate van OSA</FormLabel>
                        <InputContainer>
                            <FormParagraph>{fields.mateVanOsa}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
                {fields.mateVanZiektelast !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Mate van ziektelast</FormLabel>
                        <InputContainer>
                            <FormParagraph>{fields.mateVanZiektelast}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {fields.bmiCategorie !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>BMI categorie</FormLabel>
                        <InputContainer>
                            <FormParagraph>{fields.bmiCategorie}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {isComorbiditeitenVolgensHuisartsFilled() && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Comorbiditeiten volgens verwijzing</FormLabel>
                        <InputContainer>
                            <List readVariant="stacked">
                                {fields.comorbiditeitenVolgensHuisarts.hoge_bloeddruk && (
                                    <ListItem bold>Hoge bloeddruk</ListItem>
                                )}
                                {fields.comorbiditeitenVolgensHuisarts.cardiale_problematiek && (
                                    <ListItem bold>Cardiale problematiek</ListItem>
                                )}
                                {fields.comorbiditeitenVolgensHuisarts.diabetes_mellitus && (
                                    <ListItem bold>Diabetes mellitus</ListItem>
                                )}
                                {fields.comorbiditeitenVolgensHuisarts.burn_out_of_depressieve_klachten && (
                                    <ListItem bold>Burn-out of depressieve klachten</ListItem>
                                )}
                                {fields.comorbiditeitenVolgensHuisarts.anders_text && (
                                    <ListItem bold>{fields.comorbiditeitenVolgensHuisarts.anders_text}</ListItem>
                                )}
                            </List>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {fields.andereComorbiditeiten !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Aanvullende comorbiditeiten</FormLabel>
                        <InputContainer>
                            <FormParagraph>
                                {fields.andereComorbiditeiten
                                    ? 'Ja' +
                                      (fields.andereComorbiditeitenRemarks
                                          ? `; ${fields.andereComorbiditeitenRemarks}`
                                          : '')
                                    : 'Nee'}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <FormDivider />
                <HeaderDivider
                    text={translateKey('diagnosticReport.policy')}
                    tooltipText={showBeleidTooltip()}
                />

                {showBeleid()}

                {(fields.volgensProtocol || fields.vervolgAfspraakMetSpecialistAndersText) && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Vervolg afspraak met specialist</FormLabel>
                        <InputContainer>
                            <List readVariant="stacked">
                                {fields.volgensProtocol && <ListItem bold>Volgens protocol</ListItem>}
                                {fields.vervolgAfspraakMetSpecialistAndersText && (
                                    <ListItem bold>{fields.vervolgAfspraakMetSpecialistAndersText}</ListItem>
                                )}
                            </List>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {fields.adviesAanDeHuisarts !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Advies aan de huisarts</FormLabel>
                        <InputContainer>
                            <FormParagraph>{fields.adviesAanDeHuisarts}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {props.diagnosticReport.remark !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('common.remarks')}</FormLabel>
                        <InputContainer>
                            <FormParagraph>{props.diagnosticReport.remark}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {fields.rijbevoegdheidBesproken !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Rijbevoegdheid besproken</FormLabel>
                        <InputContainer>
                            <FormParagraph>{fields.rijbevoegdheidBesproken}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {fields.rijbevoegdheidBesprokenRemark && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Toelichting rijbevoegdheid</FormLabel>
                        <InputContainer>
                            <FormParagraph>{fields.rijbevoegdheidBesprokenRemark}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
            </>
        );
    }

    function showOsaNoFlow() {
        const toesturenValues = getProgrammeList(fields.checkedTreatments) + getToesturenValues(fields.toesturen);

        return (
            <>
                {fields.bmiCategorie !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>BMI categorie</FormLabel>
                        <InputContainer>
                            <FormParagraph>{fields.bmiCategorie}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {isComorbiditeitenVolgensHuisartsFilled() && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Comorbiditeiten volgens verwijzing</FormLabel>
                        <InputContainer>
                            <List readVariant="stacked">
                                {fields.comorbiditeitenVolgensHuisarts.hoge_bloeddruk && (
                                    <ListItem bold>Hoge bloeddruk</ListItem>
                                )}
                                {fields.comorbiditeitenVolgensHuisarts.cardiale_problematiek && (
                                    <ListItem bold>Cardiale problematiek</ListItem>
                                )}
                                {fields.comorbiditeitenVolgensHuisarts.diabetes_mellitus && (
                                    <ListItem bold>Diabetes mellitus</ListItem>
                                )}
                                {fields.comorbiditeitenVolgensHuisarts.burn_out_of_depressieve_klachten && (
                                    <ListItem bold>Burn-out of depressieve klachten</ListItem>
                                )}
                                {fields.comorbiditeitenVolgensHuisarts.anders_text && (
                                    <ListItem bold>{fields.comorbiditeitenVolgensHuisarts.anders_text}</ListItem>
                                )}
                            </List>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <FormDivider />
                <HeaderDivider text={translateKey('diagnosticReport.policy')} />

                {!!fields.adviesAanDeHuisarts && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Advies aan de huisarts</FormLabel>
                        <InputContainer>
                            <FormParagraph>{fields.adviesAanDeHuisarts}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {!!toesturenValues && (
                    <HorizontalFormGroupRead>
                        <FormLabel>E-health programma´s</FormLabel>
                        <InputContainer>
                            <FormParagraph>{toesturenValues}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {fields.behandeling1eLijnRemark !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Behandeling in de 1e lijn</FormLabel>
                        <InputContainer>
                            <FormParagraph>{fields.behandeling1eLijnRemark}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {/* This block is needed to be retrocompatible with the old toesturen field */}
                {(fields.toesturen as any) === true && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Toesturen</FormLabel>
                        <InputContainer>
                            <FormParagraph>Therapieland: module "Lekker Slapen"</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {props.diagnosticReport.remark !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('common.remarks')}</FormLabel>
                        <InputContainer>
                            <FormParagraph>{props.diagnosticReport.remark}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
            </>
        );
    }

    function showBeleidTooltip() {
        const values: ReactElement[] = [];
        if (fields.beleid.spt) {
            values.push(
                <ul>
                    <li className="listheader">SPT</li>
                    <li className="no-style">Slaap Positie Therapie</li>
                </ul>
            );
        }
        if (fields.beleid.mra) {
            values.push(
                <ul>
                    <li className="listheader">MRA</li>
                    <li className="no-style">Mandibulair Repositie Apparaat</li>
                </ul>
            );
        }
        if (fields.beleid.cpap) {
            values.push(
                <ul>
                    <li className="listheader">CPAP</li>
                    <li className="no-style">Continuous Positive Airway Pressure</li>
                </ul>
            );
        }
        if (values.length > 0) {
            return <>{...values}</>;
        } else {
            return undefined;
        }
    }

    function showBeleid() {
        if (!fields.beleid || !isBeleidFieldFilled()) {
            return null;
        }

        let cpapOptionLabel: string | undefined = undefined;
        if (fields.beleid.cpap && fields.beleid.cpapOption) {
            cpapOptionLabel = fields.beleid.cpapOption;

            if (fields.beleid.cpapOptionRemark) {
                cpapOptionLabel += `: ${fields.beleid.cpapOptionRemark}`;
            }
        }

        const toesturenValues = getProgrammeList(fields.checkedTreatments) + getToesturenValues(fields.toesturen);

        return (
            <>
                <HorizontalFormGroupRead>
                    <FormLabel>Beleid</FormLabel>
                    <InputContainer>
                        {fields.beleid.spt && (
                            <FormParagraph>
                                {['SPT', fields.beleid.sptOption, fields.beleid.sptOptionRemark]
                                    .filter((item) => !!item)
                                    .join('\n')}
                            </FormParagraph>
                        )}

                        {fields.beleid.mra && (
                            <FormParagraph>
                                {['MRA', fields.beleid.mraAbcdScore, fields.beleid.mraAbcdScoreRemark]
                                    .filter((item) => !!item)
                                    .join('\n')}
                            </FormParagraph>
                        )}

                        {fields.beleid.cpap && (
                            <FormParagraph>
                                {['CPAP', fields.beleid.cpapBeleidOption, fields.beleid.cpapBeleidOptionRemark]
                                    .filter((item) => !!item)
                                    .join('\n')}
                            </FormParagraph>
                        )}

                        {fields.beleid.adviesLifestyleVerandering && (
                            <FormParagraph>
                                {'Advies lifestyle verandering' +
                                    (fields.beleid.adviesLifestyleVeranderingRemarks
                                        ? `: ${fields.beleid.adviesLifestyleVeranderingRemarks}`
                                        : '')}
                            </FormParagraph>
                        )}
                        {!!toesturenValues && <ListItem bold>{'E-health programma´s: ' + toesturenValues}</ListItem>}
                        {fields.beleid.doorverwijzen && (
                            <FormParagraph>
                                {'Doorverwijzen' +
                                    (fields.beleid.doorverwijzenRemarks
                                        ? `: ${fields.beleid.doorverwijzenRemarks}`
                                        : '')}
                            </FormParagraph>
                        )}
                        {fields.beleid.beleidAndersText && (
                            <FormParagraph>{fields.beleid.beleidAndersText}</FormParagraph>
                        )}
                    </InputContainer>
                </HorizontalFormGroupRead>
                {fields.beleid.cpapOption && (
                    <HorizontalFormGroupRead>
                        <FormLabel>CPAP instellingen</FormLabel>
                        <InputContainer>
                            <FormParagraph>{cpapOptionLabel}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
            </>
        );
    }

    function showRelevanteBevindingen() {
        const values: string[] = [];
        if (fields.inefficienteSlaaphygiene) {
            values.push(
                'Inefficiënte slaaphygiëne' +
                    (fields.inefficienteSlaaphygieneRemarks ? `: ${fields.inefficienteSlaaphygieneRemarks}` : '')
            );
        }
        if (fields.veelBewegingen) {
            values.push('Veel bewegingen');
        }
        if (fields.slechteSlaapefficientie) {
            values.push('Niet optimale slaapefficiëntie');
        }
        if (fields.afwijkendHartritme) {
            values.push(
                'Aanwijzing hartritmestoornis' +
                    (fields.afwijkendHartritmeRemarks ? `: ${fields.afwijkendHartritmeRemarks}` : '')
            );
        }
        if (fields.overigeRelevanteBevindingenAndersText) {
            values.push(fields.overigeRelevanteBevindingenAndersText);
        }

        if (values.length > 0) {
            return (
                <HorizontalFormGroupRead>
                    <FormLabel>Overige relevante bevindingen</FormLabel>
                    <InputContainer>
                        <FormParagraph>{values.join(', ')}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            );
        }
    }

    function isComorbiditeitenVolgensHuisartsFilled() {
        return (
            fields.comorbiditeitenVolgensHuisarts.burn_out_of_depressieve_klachten ||
            fields.comorbiditeitenVolgensHuisarts.cardiale_problematiek ||
            fields.comorbiditeitenVolgensHuisarts.diabetes_mellitus ||
            fields.comorbiditeitenVolgensHuisarts.hoge_bloeddruk ||
            fields.comorbiditeitenVolgensHuisarts.anders_checked ||
            fields.comorbiditeitenVolgensHuisarts.anders_text !== null
        );
    }

    function isBeleidFieldFilled() {
        return (
            fields.beleid.spt ||
            fields.beleid.mraAbcdScore ||
            fields.beleid.cpap ||
            fields.beleid.adviesLifestyleVerandering ||
            fields.beleid.doorverwijzen ||
            fields.beleid.beleidAndersText ||
            fields.checkedTreatments?.length
        );
    }
}
